import type { Dashboard } from "@/data/dashboards";
import { useIframeLoadTime } from "@/hooks/useIframeLoadTime";
import type { PropertyDef } from "@/hooks/useProperties";
import { useTenant } from "@/hooks/useTenant";
import { useThumbnailQueue } from "@/hooks/useThumbnailQueue";
import { buildEmbedUrl } from "@/lib/dashboardViewHelpers";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  FormDialog,
} from "@simplicity/ui";
import { Archive, LayoutDashboard, MoreVertical, Pencil, Star, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";

import {
  canSaveDashboardEdit,
  DashboardEditFields,
  mapToTags,
  tagMapsEqual,
  tagsToMap,
  type TagMap,
} from "./DashboardEditFields";

interface DashboardItemProps {
  dashboard: Dashboard;
  index?: number;
  onEdit?: (id: string, updates: DashboardUpdates) => void;
  // SIM-1418 — replace the full set of typed property tags for this dashboard.
  // Server validates each (propertyKey, value) against the tenant's
  // PropertyDef.options and enforces single-select cardinality.
  onEditTags?: (id: string, tags: Array<{ propertyKey: string; value: string }>) => void;
  // SIM-1418 — tenant property definitions used to render the Properties
  // section in the Edit dialog. Undefined or empty means no Properties
  // section is shown.
  properties?: PropertyDef[];
  onArchive?: (id: string) => void;
  onDelete?: (id: string) => void;
  isStarred?: boolean;
  onToggleStar?: (id: string, currentlyStarred: boolean) => void;
}

function StarButton({
  dashboard,
  isStarred,
  hovered,
  onToggleStar,
}: {
  dashboard: Dashboard;
  isStarred: boolean;
  hovered: boolean;
  onToggleStar: (id: string, currentlyStarred: boolean) => void;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className={`absolute top-2 left-2 z-10 h-auto w-auto p-1 bg-background/80 backdrop-blur-xs border border-border shadow-xs hover:bg-accent transition-colors ${hovered || isStarred ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggleStar(dashboard.id, isStarred);
      }}
      aria-label={isStarred ? `Unstar ${dashboard.title}` : `Star ${dashboard.title}`}
    >
      <Star
        className={`h-4 w-4 ${isStarred ? "fill-star text-star" : "text-muted-foreground"}`}
        aria-hidden="true"
      />
    </Button>
  );
}

function ActionsMenu({
  dashboard,
  hovered,
  menuOpen,
  setMenuOpen,
  onArchive,
  onDelete,
  hasEdit,
  setEditOpen,
  setConfirmDelete,
}: {
  dashboard: Dashboard;
  hovered: boolean;
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  onArchive?: (id: string) => void;
  onDelete?: (id: string) => void;
  hasEdit: boolean;
  setEditOpen: (open: boolean) => void;
  setConfirmDelete: (open: boolean) => void;
}) {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- handlers only stop propagation so the action-menu click doesn't trigger the card's stretched-link navigation (SIM-898); not a real interactive surface — the inner DropdownMenu trigger Button is keyboard-accessible.
    <div
      className={`absolute top-2 right-2 z-10 transition-opacity ${hovered || menuOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto w-auto p-1 bg-background/80 backdrop-blur-xs border border-border shadow-xs hover:bg-accent transition-colors"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            aria-label={`Actions for ${dashboard.title}`}
          >
            <MoreVertical className="h-4 w-4 text-foreground" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {hasEdit && (
            <DropdownMenuItem onSelect={() => setEditOpen(true)}>
              <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
              Edit
            </DropdownMenuItem>
          )}
          {onArchive && (
            <DropdownMenuItem onSelect={() => onArchive(dashboard.id)}>
              <Archive className="h-4 w-4 mr-2" aria-hidden="true" />
              {dashboard.archived ? "Unarchive" : "Archive"}
            </DropdownMenuItem>
          )}
          {onDelete && (
            <DropdownMenuItem
              onSelect={() => setConfirmDelete(true)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

interface DashboardUpdates {
  title: string;
  url: string;
  school_year: string;
  category: string;
}

function useEditDialogState(dashboard: Dashboard, open: boolean) {
  const [editTitle, setEditTitle] = useState(dashboard.title);
  const [editUrl, setEditUrl] = useState(dashboard.url);
  const [editSchoolYear, setEditSchoolYear] = useState(dashboard.school_year);
  const [editCategory, setEditCategory] = useState(dashboard.category);
  const [tagMap, setTagMap] = useState<TagMap>(() => tagsToMap(dashboard.tags));

  // Re-seed local form state whenever the dialog opens. Without this, a user
  // could open the dialog, cancel, mutate the dashboard via another path,
  // and then re-open to find stale field values.
  useEffect(() => {
    if (open) {
      setEditTitle(dashboard.title);
      setEditUrl(dashboard.url);
      setEditSchoolYear(dashboard.school_year);
      setEditCategory(dashboard.category);
      setTagMap(tagsToMap(dashboard.tags));
    }
  }, [
    open,
    dashboard.title,
    dashboard.url,
    dashboard.school_year,
    dashboard.category,
    dashboard.tags,
  ]);

  return {
    editTitle,
    setEditTitle,
    editUrl,
    setEditUrl,
    editSchoolYear,
    setEditSchoolYear,
    editCategory,
    setEditCategory,
    tagMap,
    setTagMap,
  };
}

function EditDashboardDialog({
  dashboard,
  open,
  onOpenChange,
  properties = [],
  onEdit,
  onEditTags,
}: {
  dashboard: Dashboard;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  properties?: PropertyDef[];
  onEdit?: (id: string, updates: DashboardUpdates) => void;
  onEditTags?: (id: string, tags: Array<{ propertyKey: string; value: string }>) => void;
}) {
  const s = useEditDialogState(dashboard, open);
  const canSave = canSaveDashboardEdit({
    title: s.editTitle,
    url: s.editUrl,
    type: dashboard.type,
  });

  const handleSave = () => {
    if (!canSave || !onEdit) return;
    onEdit(dashboard.id, {
      title: s.editTitle.trim(),
      url: s.editUrl.trim(),
      school_year: s.editSchoolYear,
      category: s.editCategory,
    });
    // Only fire onEditTags if the tag selection actually changed — avoids a
    // round-trip on every save when the user didn't touch the Properties
    // section.
    const originalMap = tagsToMap(dashboard.tags);
    if (onEditTags && !tagMapsEqual(originalMap, s.tagMap)) {
      onEditTags(dashboard.id, mapToTags(s.tagMap));
    }
    onOpenChange(false);
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Dashboard"
      description="Update the dashboard's name and other properties. Changes apply immediately."
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            Save
          </Button>
        </>
      }
    >
      <DashboardEditFields
        dashboardId={dashboard.id}
        dashboardType={dashboard.type}
        editTitle={s.editTitle}
        setEditTitle={s.setEditTitle}
        editUrl={s.editUrl}
        setEditUrl={s.setEditUrl}
        editSchoolYear={s.editSchoolYear}
        setEditSchoolYear={s.setEditSchoolYear}
        editCategory={s.editCategory}
        setEditCategory={s.setEditCategory}
        properties={properties}
        tagMap={s.tagMap}
        setTagMap={s.setTagMap}
        variant="modal"
        // eslint-disable-next-line jsx-a11y/no-autofocus -- inside <FormDialog>; focus title field on open
        autoFocus
      />
    </FormDialog>
  );
}

function ThumbnailPreview({
  dashboard,
  index,
  hovered,
}: {
  dashboard: Dashboard;
  index: number;
  hovered: boolean;
}) {
  const { containerRef, shouldLoad, iframeLoaded, onIframeLoad, onIframeError } =
    useThumbnailQueue(index);
  const { onSrcSet, onLoad: onLoadTime } = useIframeLoadTime(
    dashboard.id,
    dashboard.slug || dashboard.id,
    "thumbnail",
  );

  useEffect(() => {
    if (shouldLoad) {
      onSrcSet();
    }
  }, [shouldLoad, onSrcSet]);

  const handleIframeLoad = useCallback(() => {
    onIframeLoad();
    onLoadTime();
  }, [onIframeLoad, onLoadTime]);

  return (
    <div
      ref={containerRef}
      className="w-full aspect-5/3 overflow-hidden mb-3 transition-all duration-300 relative bg-muted border border-border rounded"
      style={{ filter: hovered ? "saturate(1.2)" : "saturate(1)" }}
    >
      {/* Placeholder — visible until iframe loads */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center gap-2 transition-opacity duration-500"
        style={{ opacity: iframeLoaded ? 0 : 1 }}
      >
        <LayoutDashboard className="h-8 w-8 text-muted-foreground/40" aria-hidden="true" />
        <span className="text-xs text-muted-foreground font-medium px-4 text-center truncate max-w-full">
          {dashboard.title}
        </span>
      </div>

      {/* Iframe — loaded progressively, fades in on load */}
      {shouldLoad && (
        <div
          className="absolute inset-0 overflow-hidden pointer-events-none transition-opacity duration-500"
          style={{ opacity: iframeLoaded ? 1 : 0 }}
        >
          <iframe
            src={buildEmbedUrl(dashboard.url)}
            className="border-0 absolute top-0 left-0"
            style={{
              width: "1600px",
              height: "1280px",
              transform: "scale(var(--thumb-scale))",
              transformOrigin: "top left",
            }}
            ref={(el) => {
              if (el) {
                const parent = el.parentElement;
                if (parent) {
                  const scale = parent.clientWidth / 1600;
                  el.style.setProperty("--thumb-scale", String(scale));
                }
              }
            }}
            onLoad={handleIframeLoad}
            // A failed embed frees its concurrency slot now instead of holding
            // it until the queue's timeout reclaims it.
            onError={onIframeError}
            tabIndex={-1}
            aria-hidden="true"
            sandbox="allow-scripts allow-same-origin allow-storage-access-by-user-activation"
            title={`Thumbnail preview of ${dashboard.title}`}
          />
        </div>
      )}
    </div>
  );
}

function SparkThumbnailPlaceholder({
  dashboard,
  hovered,
}: {
  dashboard: Dashboard;
  hovered: boolean;
}) {
  return (
    <div
      className="w-full aspect-5/3 overflow-hidden mb-3 transition-all duration-300 relative bg-muted border border-border rounded flex flex-col items-center justify-center gap-2"
      style={{ filter: hovered ? "saturate(1.2)" : "saturate(1)" }}
    >
      <LayoutDashboard className="h-10 w-10 text-primary/60" aria-hidden="true" />
      <span className="text-xs font-medium text-primary">SimplySpark</span>
      <span className="text-xs text-muted-foreground px-4 text-center truncate max-w-full">
        {dashboard.title}
      </span>
    </div>
  );
}

/**
 * SIM-1916 — SimplySpark card thumbnail. Renders the snapshot captured on
 * publish (stable key `thumbnail.png`); if none exists yet the image 404s and
 * we fall back to the existing placeholder. The thumbnail key is derived from
 * the SparkDashboard id (`sparkDashboardId`), matching the upload path.
 */
function SparkThumbnail({ dashboard, hovered }: { dashboard: Dashboard; hovered: boolean }) {
  const [failed, setFailed] = useState(false);
  const sparkId = dashboard.sparkDashboardId;

  if (failed || !sparkId) {
    return <SparkThumbnailPlaceholder dashboard={dashboard} hovered={hovered} />;
  }

  const src = `/api/bi/images/${dashboard.tenantId}/${sparkId}/thumbnail.png`;

  return (
    <div
      className="w-full aspect-5/3 overflow-hidden mb-3 transition-all duration-300 relative bg-muted border border-border rounded"
      style={{ filter: hovered ? "saturate(1.2)" : "saturate(1)" }}
    >
      <img
        src={src}
        alt={`Thumbnail preview of ${dashboard.title}`}
        className="absolute inset-0 h-full w-full object-cover object-top"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

// SIM-898 — non-legacy properties render as plain display text in the same
// typography as the year/category rows. Inline double-click editing was
// removed (it conflicted with card-wide navigation); all editing flows
// through the Actions-menu → Edit dialog now.
function MetadataRow({
  dashboard,
  properties,
}: {
  dashboard: Dashboard;
  properties: PropertyDef[];
}) {
  const nonLegacy = properties.filter((p) => p.key !== "school_year" && p.key !== "category");
  const valuesByKey = new Map<string, string[]>();
  for (const tag of dashboard.tags) {
    const list = valuesByKey.get(tag.propertyKey) ?? [];
    list.push(tag.value);
    valuesByKey.set(tag.propertyKey, list);
  }

  return (
    <div className="-mt-0.5 flex flex-col gap-1 text-xs text-muted-foreground">
      <span>
        <span className="font-medium text-foreground">School Year:</span>{" "}
        <span>{dashboard.school_year || "—"}</span>
      </span>
      <span>
        <span className="font-medium text-foreground">Category:</span>{" "}
        <span>{dashboard.category || "—"}</span>
      </span>
      {nonLegacy.map((p) => {
        const values = valuesByKey.get(p.key) ?? [];
        return (
          <span key={p.key} data-testid={`property-row-${p.key}`}>
            <span className="font-medium text-foreground">{p.label}:</span>{" "}
            <span>{values.length > 0 ? values.join(", ") : "—"}</span>
          </span>
        );
      })}
    </div>
  );
}

function DeleteConfirmDialog({
  dashboard,
  confirmDelete,
  setConfirmDelete,
  onDelete,
}: {
  dashboard: Dashboard;
  confirmDelete: boolean;
  setConfirmDelete: (open: boolean) => void;
  onDelete?: (id: string) => void;
}) {
  return (
    <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Dashboard</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to permanently delete &ldquo;{dashboard.title}&rdquo;? This action
            cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onDelete?.(dashboard.id)}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DashboardCardContent({
  dashboard,
  index,
  hovered,
  properties,
  cardHref,
}: {
  dashboard: Dashboard;
  index: number;
  hovered: boolean;
  properties: PropertyDef[];
  cardHref: string;
}) {
  return (
    <>
      {dashboard.type === "spark" ? (
        <SparkThumbnail dashboard={dashboard} hovered={hovered} />
      ) : (
        <ThumbnailPreview dashboard={dashboard} index={index} hovered={hovered} />
      )}
      {/* SIM-898 — non-positioned wrapper so the stretched <Link>::after
          resolves to the card div (the nearest `relative` ancestor), covering
          the thumbnail + content surface. */}
      <div>
        <h3 className="text-sm font-semibold leading-tight py-1 text-[hsl(var(--dashboard-title))]">
          <Link to={cardHref} className="after:absolute after:inset-0">
            {dashboard.title}
          </Link>
        </h3>
        <MetadataRow dashboard={dashboard} properties={properties} />
      </div>
    </>
  );
}

function DashboardItemTopOverlays({
  dashboard,
  hovered,
  isStarred,
  onToggleStar,
  onArchive,
  onDelete,
  onEdit,
  menuOpen,
  setMenuOpen,
  setEditOpen,
  setConfirmDelete,
}: {
  dashboard: Dashboard;
  hovered: boolean;
  isStarred: boolean;
  onToggleStar?: (id: string, next: boolean) => void;
  onArchive?: (id: string) => void;
  onDelete?: (id: string) => void;
  onEdit?: (id: string, updates: DashboardUpdates) => void;
  menuOpen: boolean;
  setMenuOpen: (v: boolean) => void;
  setEditOpen: (v: boolean) => void;
  setConfirmDelete: (v: boolean) => void;
}) {
  return (
    <>
      {onToggleStar && (
        <StarButton
          dashboard={dashboard}
          isStarred={isStarred}
          hovered={hovered}
          onToggleStar={onToggleStar}
        />
      )}
      {(onArchive ?? onDelete ?? onEdit) && (
        <ActionsMenu
          dashboard={dashboard}
          hovered={hovered}
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          onArchive={onArchive}
          onDelete={onDelete}
          hasEdit={Boolean(onEdit)}
          setEditOpen={setEditOpen}
          setConfirmDelete={setConfirmDelete}
        />
      )}
    </>
  );
}

export function DashboardItem({
  dashboard,
  index = 0,
  onEdit,
  onEditTags,
  properties = [],
  onArchive,
  onDelete,
  isStarred = false,
  onToggleStar,
}: DashboardItemProps) {
  const { tenantPath } = useTenant();
  const [hovered, setHovered] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // SIM-898 — navigation is provided by a stretched react-router <Link> in the
  // card title (the ::after pseudo covers the whole card). The card div is now
  // a non-interactive container so axe's nested-interactive rule (the star +
  // actions <button>s used to be nested inside role="button") is satisfied.
  const cardHref = tenantPath(`/dashboard/${dashboard.slug || dashboard.id}`);

  return (
    <div
      data-testid="dashboard-card"
      className={`block group border rounded p-3 relative transition-all duration-200 ${hovered ? "shadow-lg -translate-y-0.5 border-primary" : "shadow-md border-border"} ${dashboard.archived ? "opacity-50" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <DashboardItemTopOverlays
        dashboard={dashboard}
        hovered={hovered}
        isStarred={isStarred}
        onToggleStar={onToggleStar}
        onArchive={onArchive}
        onDelete={onDelete}
        onEdit={onEdit}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        setEditOpen={setEditOpen}
        setConfirmDelete={setConfirmDelete}
      />
      <DashboardCardContent
        dashboard={dashboard}
        index={index}
        hovered={hovered}
        properties={properties}
        cardHref={cardHref}
      />
      <CardDialogsLayer
        dashboard={dashboard}
        confirmDelete={confirmDelete}
        setConfirmDelete={setConfirmDelete}
        onDelete={onDelete}
        editOpen={editOpen}
        setEditOpen={setEditOpen}
        properties={properties}
        onEdit={onEdit}
        onEditTags={onEditTags}
      />
    </div>
  );
}

// SIM-1443 — extracted from the card body so DashboardItem stays under the
// max-lines-per-function cap. The wrapper stops React-synthetic-event
// propagation from the portalled dialogs back into the card; the overlay
// buttons (star + actions menu) sit above the stretched link via z-10.
function CardDialogsLayer({
  dashboard,
  confirmDelete,
  setConfirmDelete,
  onDelete,
  editOpen,
  setEditOpen,
  properties,
  onEdit,
  onEditTags,
}: {
  dashboard: Dashboard;
  confirmDelete: boolean;
  setConfirmDelete: (v: boolean) => void;
  onDelete?: (id: string) => void;
  editOpen: boolean;
  setEditOpen: (v: boolean) => void;
  properties: PropertyDef[];
  onEdit?: (id: string, updates: DashboardUpdates) => void;
  onEditTags?: (id: string, tags: Array<{ propertyKey: string; value: string }>) => void;
}) {
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- handlers only stop propagation so the nested Delete/Edit dialog interactions don't bubble to the card's stretched-link navigation (SIM-898); onKeyDown is present and the nested dialogs are keyboard-accessible.
    <div
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <DeleteConfirmDialog
        dashboard={dashboard}
        confirmDelete={confirmDelete}
        setConfirmDelete={setConfirmDelete}
        onDelete={onDelete}
      />
      <EditDashboardDialog
        dashboard={dashboard}
        open={editOpen}
        onOpenChange={setEditOpen}
        properties={properties}
        onEdit={onEdit}
        onEditTags={onEditTags}
      />
    </div>
  );
}
